import React from 'react';
import { Card, Form, Button, Alert, Modal } from 'react-bootstrap';
import { get_json, post_json, delete_json } from './util.js';

const ConfirmationModal = ({ show, title, message, onConfirm, onCancel }) => (
  <Modal show={show} onHide={onCancel} centered>
    <Modal.Header className="bg-danger text-white">
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
    <Modal.Body>{message}</Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="danger" onClick={onConfirm}>
        Delete
      </Button>
    </Modal.Footer>
  </Modal>
);

const JsonModal = ({ show, data, onClose }) => (
  <Modal show={show} onHide={onClose} centered size="lg">
    <Modal.Header closeButton>
      <Modal.Title>Filter JSON Data</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <pre className="bg-light p-3 mb-0" style={{ maxHeight: '70vh', overflow: 'auto' }}>
        {JSON.stringify(data, null, 2)}
      </pre>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

class FilterRule extends React.Component {
  render() {
    const { rule = {}, onUpdate, onRemove, showRemove, features } = this.props;
    const { operator = 'IN', field_name = 'title', match_value = '', engine = 'rss' } = rule;
    const canDelete = showRemove && (match_value?.trim() || '').length > 0;
    const requiresValue = operator !== 'HAS';

    const currentEngineFields = features?.engine_fields?.[engine] || [];

    return (
      <div className="d-flex gap-2 align-items-center mb-2">
        <Form.Select
          size="sm"
          value={engine}
          onChange={(e) => {
            const newEngine = e.target.value;
            const newFields = features?.engine_fields?.[newEngine] || [];
            onUpdate({
              ...rule,
              engine: newEngine,
              field_name: newFields[0] || 'title' // Set to first available field
            });
          }}
          style={{ width: '100px' }}
        >
          {features?.engines?.map(eng => (
            <option key={eng} value={eng}>
              {eng.charAt(0).toUpperCase() + eng.slice(1)}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          size="sm"
          value={field_name}
          onChange={(e) => onUpdate({ ...rule, field_name: e.target.value })}
          style={{ width: '110px' }}
        >
          {currentEngineFields.map(field => (
            <option key={field} value={field}>
              {field.charAt(0).toUpperCase() + field.slice(1)}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          size="sm"
          value={operator}
          onChange={(e) => {
            const newOperator = e.target.value;
            onUpdate({
              ...rule,
              operator: newOperator,
              match_value: newOperator === 'HAS' ? '' : rule.match_value
            });
          }}
          style={{ width: '70px' }}
        >
          {features?.operators?.map(op => (
            <option key={op} value={op}>{op}</option>
          ))}
        </Form.Select>
        <Form.Control
          size="sm"
          type="text"
          placeholder={requiresValue ? "Value to match" : "No value needed"}
          value={match_value || ''}
          onChange={(e) => onUpdate({ ...rule, match_value: e.target.value })}
          disabled={!requiresValue}
          isInvalid={requiresValue && showRemove && !(match_value || '').trim()}
          style={{ flex: 1 }}
        />
        {showRemove && (
          <Button
            size="sm"
            variant={!(!canDelete && requiresValue) ? "danger" : "secondary"}
            onClick={onRemove}
            disabled={!canDelete && requiresValue}
            title={!canDelete && requiresValue ? "Cannot delete rule with empty value" : "Delete rule"}
          >×</Button>
        )}
      </div>
    );
  }
}

const FilterForm = ({ filter, sources, isNewFilter, onChange }) => (
  <div className="row g-2 mb-2">
    {isNewFilter ? (
      <div className="col-md-6">
        <Form.Group>
          <Form.Label className="mb-1 small">Filter Name</Form.Label>
          <Form.Control
            size="sm"
            type="text"
            value={filter.name}
            onChange={(e) => onChange({ ...filter, name: e.target.value })}
            placeholder="Enter filter name"
          />
        </Form.Group>
      </div>
    ) : null}
    <div className={isNewFilter ? "col-md-6" : "col-12"}>
      <Form.Group>
        <Form.Label className="mb-1 small">Feed Source</Form.Label>
        <Form.Select
          size="sm"
          value={filter.source}
          onChange={(e) => onChange({ ...filter, source: e.target.value })}
        >
          <option key="Any" value="Any">Any</option>
          {sources.map(source => (
            <option key={source.name} value={source.name}>
              {source.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
    </div>
    <div className="col-12">
      <Form.Group className="mb-0">
        <Form.Check
          type="checkbox"
          id="enabled"
          label="Enabled"
          checked={filter.enabled}
          onChange={(e) => onChange({ ...filter, enabled: e.target.checked })}
        />
      </Form.Group>
    </div>
    <div className="col-12">
      <Form.Group className="mb-0">
        <Form.Check
          type="checkbox"
          id="require_unique_matches"
          label="Require unique matches"
          checked={filter.require_unique_matches}
          onChange={(e) => onChange({ ...filter, require_unique_matches: e.target.checked })}
        />
      </Form.Group>
    </div>
    <div className="col-12">
      <Form.Group className="mb-0">
        <Form.Check
          type="checkbox"
          label="Download linked files on match"
          checked={filter.fetch_linked_files}
          onChange={(e) => onChange({ ...filter, fetch_linked_files: e.target.checked })}
        />
      </Form.Group>
    </div>
    <div className="col-12">
      <Form.Group className="mb-0">
        <Form.Check
          type="checkbox"
          label="Auto-increment episode on match"
          title="Automatically increment episode number (e.g. S01E01 → S01E02) after a match"
          checked={filter.auto_increment_episode || false}
          onChange={(e) => onChange({ ...filter, auto_increment_episode: e.target.checked })}
        />
      </Form.Group>
    </div>
    <div className="col-12">
      <div className="d-flex align-items-baseline">
        <Form.Check
          type="checkbox"
          label="Auto-mute on match"
          checked={filter.mute_on_match}
          onChange={(e) => onChange({ ...filter, mute_on_match: e.target.checked })}
          className="me-1"
        />
        {filter.mute_on_match && (
          <div className="d-flex align-items-baseline">
            <span className="me-2">for</span>
            <Form.Control
              size="sm"
              type="text"
              value={filter.unmute_after}
              onChange={(e) => onChange({ ...filter, unmute_after: e.target.value })}
              placeholder="e.g. 1 day"
              style={{ maxWidth: '200px' }}
            />
          </div>
        )}
      </div>
    </div>
  </div>
);

class FilterEdit extends React.Component {
  constructor(props) {
    super(props);
    const initialData = props.filter || props.initialData || {};
    this.state = {
      filter: {
        name: initialData.name || '',
        source: initialData.source || 'Any',
        enabled: initialData.enabled ?? true,
        fetch_linked_files: initialData.fetch_linked_files ?? false,
        auto_increment_episode: initialData.auto_increment_episode ?? false,
        mute_on_match: initialData.mute_on_match ?? false,
        unmute_after: initialData.unmute_after || '1 day',
        rules: (initialData.rules || []).map(rule => ({
          operator: rule?.operator || 'IN',
          field_name: rule?.field_name || 'title',
          match_value: rule?.match_value || '',
          engine: rule?.engine || 'rss'
        })) || [
          { operator: 'IN', engine: 'rss', field_name: 'title', match_value: '' }
        ],
        require_unique_matches: initialData.require_unique_matches ?? false
      },
      sources: [],
      features: null,
      error: null,
      isLoading: false,
      showJson: false
    };
  }

  componentDidMount() {
    this.loadSources();
    this.loadFeatures();
  }

  componentDidUpdate(prevProps) {
    if (this.props.initialData && this.props.initialData !== prevProps.initialData) {
      const initialData = this.props.initialData;
      this.setState({
        filter: {
          name: `${initialData.name || ''}_copy`,
          source: initialData.source || 'Any',
          enabled: initialData.enabled ?? true,
          fetch_linked_files: initialData.fetch_linked_files ?? false,
          mute_on_match: initialData.mute_on_match ?? false,
          unmute_after: initialData.unmute_after || '1 day',
          rules: (initialData.rules || []).map(rule => ({
            operator: rule?.operator || 'IN',
            field_name: rule?.field_name || 'title',
            match_value: rule?.match_value || '',
            engine: rule?.engine || 'rss'
          })),
          require_unique_matches: initialData.require_unique_matches ?? false
        }
      });
    }
  }

  loadFeatures = () => {
    get_json('/api/filter/features', (response) => {
      if (response.error) {
        this.setState({ error: response.error });
        return;
      }
      this.setState({ features: response });
    });
  }

  loadSources = () => {
    get_json('/source/list', (response) => {
      if (response.error) {
        this.setState({ error: response.error });
        return;
      }
      this.setState({ sources: response });
    });
  }

  handleUpdateRule = (index, updatedRule) => {
    this.setState(state => ({
      filter: {
        ...state.filter,
        rules: state.filter.rules.map((rule, i) =>
          i === index ? updatedRule : rule
        )
      }
    }));
  }

  handleRemoveRule = (index) => {
    if (this.state.filter.rules.length <= 1) {
      return;
    }

    this.setState(state => ({
      filter: {
        ...state.filter,
        rules: state.filter.rules.filter((_, i) => i !== index)
      }
    }));
  }

  handleAddRule = () => {
    const { features } = this.state;
    const defaultEngine = features?.engines?.[0] || 'rss';
    const defaultField = features?.engine_fields?.[defaultEngine]?.[0] || 'title';

    this.setState(state => ({
      filter: {
        ...state.filter,
        rules: [
          ...state.filter.rules,
          {
            operator: 'IN',
            engine: defaultEngine,
            field_name: defaultField,
            match_value: ''
          }
        ]
      }
    }));
  }

  handleSubmit = (e) => {
    e.preventDefault();

    const { filter } = this.state;
    const isNewFilter = !this.props.filter;

    // Validate required fields for new filters
    if (isNewFilter && !filter.name) {
      this.setState({ error: "Filter name is required" });
      return;
    }

    // Filter out rules with empty values and validate rule structure
    const validRules = filter.rules.filter(rule => {
      if (!rule.operator || !rule.field_name) {
        return false;
      }
      // Only require match_value for operators other than HAS
      if (rule.operator !== 'HAS' && !rule.match_value?.trim()) {
        return false;
      }
      return true;
    });

    // Ensure at least one valid rule
    if (validRules.length === 0) {
      this.setState({ error: "At least one complete filter rule is required" });
      return;
    }

    const filterToSubmit = {
      ...filter,
      rules: validRules
    };

    this.setState({ isLoading: true, error: null });

    const endpoint = isNewFilter ? '/filter/add' : `/filter/${filter.name}/update`;
    post_json(endpoint, filterToSubmit, (response) => {
      this.setState({ isLoading: false });

      if (response.error) {
        this.setState({ error: response.error });
        return;
      }

      // Filter out the message field from the response
      const { message, ...filterData } = response;

      if (isNewFilter) {
        // Reset form for new filters
        const { features } = this.state;
        const defaultEngine = features?.engines?.[0] || 'rss';
        const defaultField = features?.engine_fields?.[defaultEngine]?.[0] || 'title';

        this.setState({
          filter: {
            name: "",
            source: "Any",
            enabled: true,
            fetch_linked_files: false,
            auto_increment_episode: false,
            mute_on_match: false,
            unmute_after: '1 day',
            rules: [
              { operator: 'IN', engine: defaultEngine, field_name: defaultField, match_value: '' }
            ],
            require_unique_matches: false
          },
          error: null
        });

        if (this.props.onAdd) {
          this.props.onAdd();
        }
      }
    });
  }

  render() {
    const { filter, error, isLoading, sources, features, showJson } = this.state;
    const { filter: existingFilter } = this.props;
    const isNewFilter = !existingFilter;

    if (isLoading && !features) {
      return (
        <div className="text-center py-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    const content = (
      <Form onSubmit={this.handleSubmit}>
        <FilterForm
          filter={filter}
          sources={sources}
          isNewFilter={isNewFilter}
          onChange={(updatedFilter) => this.setState({ filter: updatedFilter })}
        />

        <Form.Group className="mb-2">
          <Form.Label className="mb-1 small">Filter Rules</Form.Label>
          <div>
            {filter.rules.map((rule, index) => (
              <FilterRule
                key={index}
                rule={rule}
                features={features}
                onUpdate={(updatedRule) => this.handleUpdateRule(index, updatedRule)}
                onRemove={() => this.handleRemoveRule(index)}
                showRemove={filter.rules.length > 1}
              />
            ))}
          </div>
        </Form.Group>

        <div className="d-flex flex-column">
          <div className="d-flex justify-content-between align-items-center">
            <Button variant="secondary" size="sm" onClick={this.handleAddRule}>
              Add Rule
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (isNewFilter ? 'Adding...' : 'Updating...') : (isNewFilter ? 'Add Filter' : 'Update')}
            </Button>
          </div>
          {error && (
            <Alert variant="danger" className="mt-2 mb-0">
              {error}
            </Alert>
          )}
        </div>
      </Form>
    );

    if (isNewFilter) {
      return content;
    }

    return (
      <Card className="h-100 card-inner">
        <Card.Header className="d-flex justify-content-between align-items-center py-2">
          <span className="text-truncate me-2" title={filter.name}>
            {filter.name}
            {filter.muted && <span className="badge bg-warning text-dark ms-2">Muted</span>}
          </span>
          <div className="flex-shrink-0">
            {filter.source && filter.source !== 'Any' && (
              <Button
                variant="info"
                size="sm"
                onClick={() => this.props.onForceSync?.(filter.name)}
                className="me-2"
                title="Force sync this filter's source"
              >
                Force Sync
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={() => this.props.onTestFilter?.(filter.name)}
              className="me-2"
              title="Test filter against recent entries"
            >
              Test Filter
            </Button>
            <Button
              variant={filter.muted ? "success" : "warning"}
              size="sm"
              onClick={() => this.props.onToggleMute?.(filter.name, filter.muted)}
              className="me-2"
              title={filter.muted ? "Unmute filter" : "Mute filter"}
            >
              {filter.muted ? "Unmute" : "Mute"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => this.setState({ showJson: true })}
              className="me-2"
            >
              Show JSON
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => this.props.onCopyToNew?.({
                ...filter,
                rules: filter.rules.map(rule => ({ ...rule }))
              })}
              className="me-2"
            >
              Copy to new
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => this.props.onDelete?.(filter.name)}
            >
              Delete
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="py-2">
          {content}
        </Card.Body>
        <JsonModal
          show={showJson}
          data={filter}
          onClose={() => this.setState({ showJson: false })}
        />
      </Card>
    );
  }
}

class Filters extends React.Component {
  constructor() {
    super();
    this.state = {
      filters: [],
      isLoading: true,
      error: null,
      copyData: null,
      showDeleteModal: false,
      filterToDelete: null,
      showTestResults: false,
      testResults: null,
      testingFilter: null
    };
  }

  componentDidMount() {
    this.loadFilters();
  }

  loadFilters = () => {
    // Get the list of filters (full objects)
    get_json('/filter/list', (response) => {
      if (response.error) {
        this.setState({ error: response.error, isLoading: false });
        return;
      }

      // Response already contains full filter objects
      this.setState({
        filters: response || [],
        isLoading: false
      });
    });
  }

  handleDeleteFilter = (name) => {
    this.setState({
      showDeleteModal: true,
      filterToDelete: name
    });
  }

  handleConfirmDelete = () => {
    const { filterToDelete } = this.state;

    this.setState({ error: null }); // Clear error before request
    delete_json(`/filter/${filterToDelete}/remove`, (response) => {
      if (response.error) {
        this.setState({
          error: response.error,
          showDeleteModal: false,
          filterToDelete: null
        });
        return;
      }
      this.setState({
        showDeleteModal: false,
        filterToDelete: null,
        error: null
      }, () => this.loadFilters());
    });
  }

  handleCancelDelete = () => {
    this.setState({
      showDeleteModal: false,
      filterToDelete: null
    });
  }

  handleCopyToNew = (filterData) => {
    this.setState({ copyData: filterData });
  }

  handleForceSync = (filterName) => {
    this.setState({ error: null, isLoading: true });
    post_json(`/filter/${filterName}/force-sync`, {}, (response) => {
      if (response.error) {
        this.setState({
          error: `Force sync failed: ${response.error}`,
          isLoading: false
        });
        return;
      }
      this.setState({
        error: null,
        isLoading: false
      });
      // Show success message
      alert(`Force sync completed!\nEntries: ${response.entry_count}\nMatches: ${response.match_count}`);
    });
  }

  handleToggleMute = (filterName, currentlyMuted) => {
    const action = currentlyMuted ? 'unmute' : 'mute';
    this.setState({ error: null });

    post_json(`/filter/${filterName}/${action}`, {}, (response) => {
      if (response.error) {
        this.setState({ error: `Failed to ${action} filter: ${response.error}` });
        return;
      }
      // Reload filters to show updated state
      this.loadFilters();
    });
  }

  handleTestFilter = (filterName) => {
    this.setState({ 
      testingFilter: filterName,
      testResults: null,
      error: null
    });
    
    post_json(`/filter/${filterName}/test`, {}, (response) => {
      if (response.error) {
        this.setState({ 
          error: `Filter test failed: ${response.error}`,
          testingFilter: null 
        });
        return;
      }
      
      this.setState({
        testResults: response,
        showTestResults: true,
        testingFilter: null
      });
    });
  }

  render() {
    const {
      filters,
      isLoading,
      error,
      copyData,
      showDeleteModal,
      filterToDelete,
      showTestResults,
      testResults,
      testingFilter
    } = this.state;

    return (
      <div className="container-fluid px-4">
        <h2 className="mb-4">Filters</h2>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <Card className="mb-4 card-primary">
          <Card.Header>
            <h5 className="mb-0">Add New Filter</h5>
          </Card.Header>
          <Card.Body>
            <FilterEdit
              onAdd={() => {
                this.setState({ copyData: null });
                this.loadFilters();
              }}
              initialData={copyData}
            />
          </Card.Body>
        </Card>

        <Card className="mb-4 card-secondary">
          <Card.Header>
            <h5 className="mb-0">Edit Existing Filters</h5>
          </Card.Header>
          <Card.Body className="p-3">
            {isLoading ? (
              <div className="text-center py-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                {filters.length === 0 ? (
                  <Alert variant="info" className="m-3">
                    No filters available. Add your first filter using the form above.
                  </Alert>
                ) : (
                  <div className="row g-3">
                    {filters.map(filter => (
                      <div key={filter.name} className="col-12 col-lg-8 col-xl-6">
                        <FilterEdit
                          filter={filter}
                          onDelete={() => this.handleDeleteFilter(filter.name)}
                          onCopyToNew={this.handleCopyToNew}
                          onForceSync={this.handleForceSync}
                          onToggleMute={this.handleToggleMute}
                          onTestFilter={this.handleTestFilter}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        {/* Test Results Modal */}
        <Modal 
          show={showTestResults} 
          onHide={() => this.setState({ showTestResults: false, testResults: null })}
          size="xl"
          dialogClassName="modal-90w"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Filter Test Results - "{testResults?.filter_name}"
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {testResults && (
              <>
                {/* Summary */}
                <Card className="mb-3">
                  <Card.Body>
                    <h6>Summary</h6>
                    <ul className="mb-0">
                      <li>Tested: <strong>{testResults.entries_tested}</strong> entries</li>
                      <li>Would Match: <strong className="text-success">{testResults.entries_matched}</strong></li>
                      <li>Blocked: <strong className="text-warning">{testResults.entries_blocked}</strong> (rules passed but other checks failed)</li>
                      <li>No Match: <strong>{testResults.entries_tested - testResults.entries_matched - testResults.entries_blocked}</strong></li>
                    </ul>
                  </Card.Body>
                </Card>

                {/* Results */}
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  {testResults.results.map((result, idx) => (
                    <Card key={idx} className="mb-2">
                      <Card.Body className="py-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <span className={`badge ${result.matched ? 'bg-success' : result.details.rules_check.passed ? 'bg-warning' : 'bg-secondary'} me-2`}>
                              {result.matched ? '✅ Match' : result.details.rules_check.passed ? '⚠️ Blocked' : '❌ No Match'}
                            </span>
                            <strong className="text-truncate d-inline-block" style={{maxWidth: '500px'}} title={result.entry_title}>
                              {result.entry_title}
                            </strong>
                            <br/>
                            <small className="text-muted">
                              GUID: {result.entry_guid} | Added: {new Date(result.entry_added).toLocaleString()}
                            </small>
                          </div>
                          <Button 
                            size="sm" 
                            variant="link"
                            onClick={() => {
                              const newResults = {...testResults};
                              newResults.results[idx].showDetails = !newResults.results[idx].showDetails;
                              this.setState({ testResults: newResults });
                            }}
                          >
                            {result.showDetails ? 'Hide Details ▲' : 'Show Details ▼'}
                          </Button>
                        </div>
                        
                        {result.showDetails && (
                          <div className="mt-2 ms-4">
                            <small>
                              <div className={result.details.enabled_check.passed ? 'text-success' : 'text-danger'}>
                                {result.details.enabled_check.passed ? '✅' : '❌'} Filter {result.details.enabled_check.passed ? 'enabled' : 'disabled'}
                              </div>
                              <div className={result.details.muted_check.passed ? 'text-success' : 'text-danger'}>
                                {result.details.muted_check.passed ? '✅' : '❌'} Filter {result.details.muted_check.passed ? 'not muted' : 'muted'}
                              </div>
                              <div className={result.details.source_check.passed ? 'text-success' : 'text-danger'}>
                                {result.details.source_check.passed ? '✅' : '❌'} Source {result.details.source_check.passed ? 'matches' : 'mismatch'}
                                {!result.details.source_check.passed && (
                                  <span> (expected: {result.details.source_check.expected}, got: {result.details.source_check.actual})</span>
                                )}
                              </div>
                              
                              {/* Enhanced Rules Check with Parsed Data */}
                              <div className={result.details.rules_check.passed ? 'text-success' : 'text-danger'}>
                                {result.details.rules_check.passed ? '✅' : '❌'} Rules {result.details.rules_check.passed ? 'match' : "don't match"}
                                
                                {/* Show parsed data from engines */}
                                {result.details.rules_check.parsed_data && (
                                  <div className="mt-2 ms-3">
                                    <div className="text-muted mb-1"><strong>Parsed Data:</strong></div>
                                    {Object.entries(result.details.rules_check.parsed_data).map(([engineName, fields]) => (
                                      <div key={engineName} className="mb-2">
                                        <div className="text-info" style={{fontSize: '0.85em'}}>
                                          <strong>{engineName}:</strong>
                                        </div>
                                        <div className="ms-3" style={{fontSize: '0.85em', fontFamily: 'monospace'}}>
                                          {Object.entries(fields).slice(0, 15).map(([k, v]) => (
                                            <div key={k} className="text-muted">
                                              {k}: <span className="text-dark">{v}</span>
                                            </div>
                                          ))}
                                          {Object.keys(fields).length > 15 && (
                                            <div className="text-muted">... and {Object.keys(fields).length - 15} more fields</div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Show individual rule evaluations */}
                                {result.details.rules_check.rule_evaluations && (
                                  <div className="mt-2 ms-3">
                                    <div className="text-muted mb-1"><strong>Rule Evaluations:</strong></div>
                                    {result.details.rules_check.rule_evaluations.map((ruleEval, idx) => (
                                      <div key={idx} className={`mb-1 ${ruleEval.passed ? 'text-success' : 'text-danger'}`}>
                                        <div>
                                          {ruleEval.passed ? '✓' : '✗'} Rule {idx + 1}: 
                                          <span className="text-info"> {ruleEval.field_name}</span>
                                          <span className="text-muted"> ({ruleEval.engine})</span>
                                        </div>
                                        <div className="ms-3" style={{fontSize: '0.9em'}}>
                                          {ruleEval.actual_value !== null ? (
                                            <>
                                              Actual: "<strong>{ruleEval.actual_value}</strong>"<br/>
                                              Expected: {ruleEval.operator} "<strong>{ruleEval.match_value}</strong>"<br/>
                                              <span className={ruleEval.passed ? 'text-success' : 'text-danger'}>
                                                {ruleEval.reason}
                                              </span>
                                            </>
                                          ) : (
                                            <span className="text-danger">{ruleEval.reason}</span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              <div className={result.details.unique_check.passed ? 'text-success' : 'text-danger'}>
                                {result.details.unique_check.passed ? '✅' : '❌'} Unique check {result.details.unique_check.passed ? 'passed' : 'BLOCKED'}
                                {!result.details.unique_check.passed && (
                                  <div className="ms-3">
                                    <div>{result.details.unique_check.reason}</div>
                                    <div>Already matched on: {new Date(result.details.unique_check.existing_match_date).toLocaleString()}</div>
                                  </div>
                                )}
                              </div>
                              {result.details.would_auto_increment && (
                                <div className="text-info mt-1">
                                  💡 Would auto-increment: {result.details.current_episode} → {result.details.new_episode}
                                </div>
                              )}
                            </small>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => this.setState({ showTestResults: false, testResults: null })}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <ConfirmationModal
          show={showDeleteModal}
          title="Delete Filter"
          message={`Are you sure you want to delete the filter "${filterToDelete}"?`}
          onConfirm={this.handleConfirmDelete}
          onCancel={this.handleCancelDelete}
        />
      </div>
    );
  }
}

export default Filters;
